/**
 * Journal construction and balanced-entry validation (ARCHITECTURE §3.1).
 *
 * The core rule of double-entry: an entry may only post when the sum of debits
 * equals the sum of credits (in the entry's currency). This module is the gate.
 */
import type { JournalEntry, JournalLine, Money } from "./types";
import { add, zero, subtract, abs, isZero } from "./money";

export interface BalanceCheck {
  readonly debitTotal: Money;
  readonly creditTotal: Money;
  /** debitTotal − creditTotal. Zero when balanced. */
  readonly delta: Money;
  readonly balanced: boolean;
  /** True when there are no lines carrying any amount. */
  readonly empty: boolean;
}

/** Total debits across lines, in the given currency. */
export function sumDebits(
  lines: readonly JournalLine[],
  currency: Money["currency"],
): Money {
  return lines.reduce((acc, l) => add(acc, l.debit), zero(currency));
}

/** Total credits across lines, in the given currency. */
export function sumCredits(
  lines: readonly JournalLine[],
  currency: Money["currency"],
): Money {
  return lines.reduce((acc, l) => add(acc, l.credit), zero(currency));
}

/** Check whether a set of lines balances, with running totals for the UI meter. */
export function checkBalance(
  lines: readonly JournalLine[],
  currency: Money["currency"],
): BalanceCheck {
  const debitTotal = sumDebits(lines, currency);
  const creditTotal = sumCredits(lines, currency);
  const delta = subtract(debitTotal, creditTotal);
  const empty = isZero(debitTotal) && isZero(creditTotal);
  return {
    debitTotal,
    creditTotal,
    delta,
    balanced: isZero(delta) && !empty,
    empty,
  };
}

export interface ValidationResult {
  readonly ok: boolean;
  readonly errors: readonly string[];
}

/**
 * Validate a full entry before posting. Enforces: balanced, at least two lines,
 * every line has exactly one non-zero side, and a non-empty description.
 */
export function validateEntry(entry: JournalEntry): ValidationResult {
  const errors: string[] = [];

  if (entry.lines.length < 2) {
    errors.push("An entry needs at least two lines.");
  }
  if (entry.description.trim() === "") {
    errors.push("A description is required.");
  }

  for (const [i, line] of entry.lines.entries()) {
    const dr = !isZero(line.debit);
    const cr = !isZero(line.credit);
    if (dr && cr) {
      errors.push(`Line ${i + 1}: a line cannot be both debit and credit.`);
    }
    if (!dr && !cr) {
      errors.push(`Line ${i + 1}: enter a debit or a credit.`);
    }
    if (line.debit.amount < 0 || line.credit.amount < 0) {
      errors.push(`Line ${i + 1}: amounts must be positive.`);
    }
  }

  const check = checkBalance(entry.lines, entry.txnCurrency);
  if (check.empty) {
    errors.push("The entry has no amounts.");
  } else if (!check.balanced) {
    errors.push(
      `Out of balance by ${abs(check.delta).amount / 100} — debits must equal credits.`,
    );
  }

  return { ok: errors.length === 0, errors };
}
